"use client";

import React, { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { FileAudio, FileIcon, FileTextIcon, Lock } from "lucide-react";
import { AttachedFile } from "@/types";
import { Button, Dialog, DialogContent, DialogTitle } from "@/app/components/ui";

interface ChatAttachmentsProps {
   files?: AttachedFile[];
}

export const ChatAttachments: React.FC<ChatAttachmentsProps> = ({ files }) => {
   const [openedImage, setOpenedImage] = useState<AttachedFile | null>(null);

   if (!files?.length) return null;

   return (
      <div className="mt-2 first:mt-0">
         <div className="flex flex-wrap gap-2">
            {files.map((file) => (
               <Preview key={file.id} file={file} onOpenImage={setOpenedImage} />
            ))}
         </div>

         <Dialog
            open={Boolean(openedImage)}
            onOpenChange={(isOpen) => !isOpen && setOpenedImage(null)}
         >
            <DialogContent className="max-w-[95vw] p-3 sm:max-w-3xl">
               <DialogTitle className="truncate pr-8 text-sm font-medium">
                  {openedImage?.name}
               </DialogTitle>
               {openedImage && (
                  <div className="relative h-[70vh] w-full">
                     <Image
                        src={openedImage.url}
                        alt={openedImage.name}
                        fill
                        unoptimized
                        className="object-contain"
                        sizes="95vw"
                     />
                  </div>
               )}
            </DialogContent>
         </Dialog>
      </div>
   );
};

const formatFileSize = (bytes: number) => {
   if (bytes === 0) return "0 B";
   const units = ["B", "KB", "MB", "GB"];
   const exponent = Math.floor(Math.log(bytes) / Math.log(1024));
   return `${parseFloat((bytes / Math.pow(1024, exponent)).toFixed(2))} ${units[exponent]}`;
};

const getFileIcon = (mimetype: string) => {
   if (mimetype.startsWith("audio/")) return <FileAudio className="h-3.5 w-3.5" />;
   if (mimetype === "application/pdf" || mimetype.startsWith("application/ms")) {
      return <FileTextIcon className="h-3.5 w-3.5" />;
   }
   return <FileIcon className="h-3.5 w-3.5" />;
};

const Preview = ({
   file,
   onOpenImage
}: {
   file: AttachedFile;
   onOpenImage: (file: AttachedFile) => void;
}) => {
   const translate = useTranslations("Home");

   if (file.restricted) {
      return (
         <div
            role="group"
            title={translate("attachmentRestricted")}
            aria-label={translate("attachmentRestricted")}
            className="flex h-12 max-w-[13rem] cursor-not-allowed items-center gap-2 rounded-lg border border-dashed border-border px-2 text-muted-foreground"
         >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
               <Lock className="h-3.5 w-3.5" />
            </span>
            <span className="flex min-w-0 flex-col">
               <span className="truncate text-xs font-medium">{file.name}</span>
               <span className="text-[10px]">{formatFileSize(file.size)}</span>
            </span>
         </div>
      );
   }

   if (file.mimetype.startsWith("image/")) {
      return (
         <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenImage(file)}
            aria-label={translate("viewFullImage")}
            title={file.name}
            className="relative h-12 w-12 overflow-hidden rounded-lg border border-border p-0 transition-opacity hover:opacity-80"
         >
            <Image
               src={file.url}
               alt={file.name}
               fill
               unoptimized
               className="object-cover"
               sizes="48px"
            />
         </Button>
      );
   }

   // Sized like the image tile beside it so a mixed set reads as one row of cards, and
   // width-capped so a long filename cannot stretch the row across the whole bubble.
   return (
      <a
         href={file.url}
         target="_blank"
         rel="noopener noreferrer"
         title={`${file.name} — ${translate("download")}`}
         className="flex h-12 max-w-[13rem] items-center gap-2 rounded-lg border border-border px-2 transition-colors hover:border-primary/40 hover:bg-primary/10"
      >
         <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            {getFileIcon(file.mimetype)}
         </span>
         <span className="flex min-w-0 flex-col">
            <span className="truncate text-xs font-medium text-foreground">
               {file.name}
            </span>
            <span className="text-[10px] text-muted-foreground">
               {formatFileSize(file.size)}
            </span>
         </span>
      </a>
   );
};
